import React from 'react';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { AdminBookingReportVO } from '../../../types/model/valueObject/booking/AdminBookingReportVO';
import { formatEndDateDisplay } from '../../../util/DateUtils';
import { formatDurationType } from '../../../util/BookingUtil';
import StatusBadge from '../../StatusBadge';
import { CollapsableCardExpandIconButton } from '../../cardComponents';

const FILLER_VALUE = 'N/A';

interface AdminBookingSummaryProps {
  item: AdminBookingReportVO;
  expanded: boolean;
}

interface IdentityProps {
  label: string;
  value?: string | null;
}

const hasValue = (value: unknown) =>
  value !== null && value !== undefined && String(value).trim() !== '';

const labelSx = {
  fontSize: '0.66rem',
  fontWeight: 700,
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  color: 'rgba(0, 0, 0, 0.6)',
  mr: 0.75,
} as const;

const Identity = ({ label, value }: IdentityProps) => (
  <Box component="span" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
    <Typography component="span" sx={labelSx}>{label}</Typography>
    {hasValue(value) ? value : FILLER_VALUE}
  </Box>
);

const formatBookedDates = (item: AdminBookingReportVO) => {
  if (!hasValue(item?.startDate)) return FILLER_VALUE;
  const end = hasValue(item?.endDate) && moment(item.endDate).isValid()
    ? formatEndDateDisplay(item.endDate, 'MM/DD/YYYY', false)
    : FILLER_VALUE;
  return `${item.startDate} - ${end}`;
};

/**
 * Collapsed-state summary shown in the header of an admin booking card.
 * @param item the AdminBookingReportVO to display
 * @param expanded whether the card is currently expanded
 */
const AdminBookingSummary = ({ item, expanded }: AdminBookingSummaryProps) => {
  const spaces = hasValue(item?.numberOfSpaces) ? item.numberOfSpaces : FILLER_VALUE;
  const bookingLabel = hasValue(item?.orderNumber) ? item.orderNumber : FILLER_VALUE;

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', width: '100%', minWidth: 0 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'row', sm: 'column' },
          alignItems: 'center',
          justifyContent: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1, sm: 0.5 },
          minWidth: { sm: 110 },
          px: { xs: 1.75, sm: 2.25 },
          py: 1.5,
          bgcolor: '#eef0fb',
          borderRight: { sm: '1px solid #e2e5f5' },
          textAlign: 'center',
          overflowWrap: 'anywhere',
        }}
      >
        <Typography component="span" sx={{ ...labelSx, mr: 0, color: 'primary.main' }}>Booking #</Typography>
        <Typography component="span" sx={{ fontSize: { xs: 17, sm: 20 }, fontWeight: 700, color: 'text.primary' }}>
          {hasValue(item?.orderNumber) ? item.orderNumber : FILLER_VALUE}
        </Typography>
        {/* Mobile only: decorative indicator; the whole header is the tap target */}
        <Box
          component="span"
          aria-hidden
          sx={{ display: { xs: 'flex', sm: 'none' }, ml: 'auto', color: 'primary.main' }}
        >
          {expanded ? <RemoveIcon /> : <AddIcon />}
        </Box>
      </Box>

      <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1.5, px: { xs: 1.75, sm: 3 }, py: 2, minWidth: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            flexWrap: 'wrap',
            alignItems: { xs: 'flex-start', sm: 'center' },
            columnGap: 3.5,
            rowGap: 0.5,
            fontSize: { xs: '0.92rem', sm: '1.05rem' },
            fontWeight: 700,
            color: 'text.primary',
          }}
        >
          <Identity label="Supplier" value={item?.supplierCompanyName} />
          <Identity label="Buyer" value={item?.buyerCompanyName} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 2.25,
            rowGap: 1,
            fontSize: { xs: '0.78rem', sm: '0.86rem' },
            fontWeight: 500,
            color: 'text.primary',
          }}
        >
          {hasValue(item?.status) ? <StatusBadge centered>{item.status}</StatusBadge> : FILLER_VALUE}
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CalendarMonthIcon aria-hidden fontSize="inherit" sx={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.6)' }} />
            <span>
              <Box component="span" sx={visuallyHidden}>Booked dates: </Box>
              {formatBookedDates(item)}
            </span>
          </Box>
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <LocalShippingIcon aria-hidden fontSize="inherit" sx={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.6)' }} />
            <span>
              <Box component="span" sx={visuallyHidden}>Spaces booked: </Box>
              {spaces}
            </span>
          </Box>
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            <Box component="span" sx={visuallyHidden}>Booking duration: </Box>
            {formatDurationType(item?.durationType) ?? FILLER_VALUE}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center', pr: { sm: 3 } }}>
        <CollapsableCardExpandIconButton
          expanded={expanded}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} details for booking ${bookingLabel}`}
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
            '& .MuiSvgIcon-root': { color: 'primary.contrastText' },
          }}
        />
      </Box>
    </Box>
  );
};

export { AdminBookingSummary };
